import { calendarDisplayDate, calendarMinuteOffset } from '../../../../utils/date-time';
import moment from 'moment/moment';
import { useAgendaContext } from '../ContextAgenda';
import { AgendaEstabelecimentoDate } from './List';
import { Modal } from '../../../../components/Modal';
import React, { useEffect, useMemo, useState } from 'react';
import { Loading } from '../../../../components/Loading';
import { AtendentesService } from '../../../atendentes/Service';
import { useAppContext } from '../../../../contexts/AppContext';
import { useFilterApp } from '../../../../contexts/FilterApp';
import { AgendaForm } from '../Form/Form';
import { UserService } from '../../../configuracoes/usuario/Service';
import { OfertasHorarioService } from '../../../ofertas-horario/Service';
import { Lock, Megaphone, Plus, X } from 'lucide-react';

const atendentesService = new AtendentesService();
const userService = new UserService();
const ofertasService = new OfertasHorarioService();

type ClientOption = {
    value: string;
    label: string;
    photoURL?: string;
    phoneNumber?: string;
};

interface ExtraActionsAgendaProps {
    agenda: AgendaEstabelecimentoDate;
}

export const ExtraActionsAgenda = ({ agenda }: ExtraActionsAgendaProps) => {
    const [open, setOpen] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [openOffer, setOpenOffer] = useState(false);
    const [loading, setLoading] = useState(false);
    const [offerLoading, setOfferLoading] = useState(false);
    const [offerQuota, setOfferQuota] = useState<any>(null);
    const [offerDiscountPercent, setOfferDiscountPercent] = useState<number>(0);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [clientsAll, setClientsAll] = useState<ClientOption[]>([]);
    const [clientsSelected, setClientsSelected] = useState<string[]>([]);
    const [clientsSearch, setClientsSearch] = useState('');
    const { attendantId } = useFilterApp();
    const { errorSnackbar, successSnackbar } = useAppContext();
    const { selectedRange, setSelectedRange, schedules, loadSettings, date, settingsAgenda } = useAgendaContext();

    const appointments = schedules?.map(schedule => ({
        title: schedule?.client?.name || 'Sem nome',
        startDate: schedule?.startAt ? calendarDisplayDate(schedule.startAt, schedule.timezone) : new Date(),
        endDate: schedule?.endAt ? calendarDisplayDate(schedule.endAt, schedule.timezone) : new Date(),
        schedule,
    }));

    // ✅ Verifica conflito com agendamentos
    const hasConflict = useMemo(() => {
        if (!selectedRange) return false;

        const start = selectedRange.start;
        const end = selectedRange.end;

        return appointments?.some(({ startDate, endDate, schedule }) => {
            const now = moment(date.toDate()).startOf('days');
            const apptStart = calendarMinuteOffset(schedule.startAt, now.format('YYYY-MM-DD'), schedule.timezone);
            const apptEnd = calendarMinuteOffset(schedule.endAt, now.format('YYYY-MM-DD'), schedule.timezone);

            return apptStart < end && apptEnd > start; // sobreposição
        });
    }, [selectedRange, appointments, date]);

    const formatTime = (minutes: any) =>
        moment.utc().startOf('day').add(minutes, 'minutes').format('HH:mm');

    const formatPhone = (raw: string) => {
        const digits = String(raw || '').replace(/\D/g, '');
        // +55DD9XXXXXXXX ou 55DD9XXXXXXXX -> (DD) 9XXXX-XXXX
        if (digits.length === 13 && digits.startsWith('55')) {
            const ddd = digits.slice(2, 4);
            const part1 = digits.slice(4, 9);
            const part2 = digits.slice(9, 13);
            return `(${ddd}) ${part1}-${part2}`;
        }
        // DD9XXXXXXXX (11) ou DDXXXXXXXX (10)
        if (digits.length === 11) {
            const ddd = digits.slice(0, 2);
            const part1 = digits.slice(2, 7);
            const part2 = digits.slice(7, 11);
            return `(${ddd}) ${part1}-${part2}`;
        }
        if (digits.length === 10) {
            const ddd = digits.slice(0, 2);
            const part1 = digits.slice(2, 6);
            const part2 = digits.slice(6, 10);
            return `(${ddd}) ${part1}-${part2}`;
        }
        return raw;
    };

    const offerDateKey = useMemo(() => moment(agenda.date).format('YYYY-MM-DD'), [agenda.date]);

    const offerDate = useMemo(() => {
        return moment(offerDateKey, 'YYYY-MM-DD').startOf('day').toDate();
    }, [offerDateKey]);

    const remainingQuota = useMemo(() => {
        const remaining = Number(offerQuota?.remaining ?? 0);
        return Number.isFinite(remaining) ? remaining : 0;
    }, [offerQuota?.remaining]);

    const maxSelectableClients = useMemo(() => {
        // Regra do MVP: ate 10 envios/dia; e a UI deve respeitar o saldo restante do dia.
        const max = Math.min(10, remainingQuota);
        return Math.max(0, max);
    }, [remainingQuota]);

    const clientsOptions = useMemo(() => {
        const term = clientsSearch.trim().toLowerCase();
        if (!term) return clientsAll;

        return clientsAll.filter((c) => {
            const name = String(c?.label || '').toLowerCase();
            const phone = String(c?.phoneNumber || '').toLowerCase();
            return name.includes(term) || phone.includes(term);
        });
    }, [clientsAll, clientsSearch]);

    const rangeOfferEligibility = useMemo(() => {
        if (!selectedRange) return { ok: false, reason: 'Selecione uma faixa.' };

        const horario = Array.isArray(settingsAgenda?.horario) ? settingsAgenda.horario : [];
        if (!horario.length) {
            return { ok: false, reason: 'Fora da janela configurada.' };
        }

        const inSomeWindow = horario.some((w: any) => {
            const s = Number(w?.start);
            const e = Number(w?.end);
            if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
            return selectedRange.start >= s && selectedRange.end <= e;
        });

        if (!inSomeWindow) {
            return { ok: false, reason: 'Fora da janela configurada.' };
        }

        const closedSlots = Array.isArray(settingsAgenda?.closedSlots) ? settingsAgenda.closedSlots : [];
        const overlapsClosed = closedSlots.some((slot: any) => {
            const s = Number(slot?.start);
            const e = Number(slot?.end);
            if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
            return selectedRange.start < e && selectedRange.end > s;
        });

        if (overlapsClosed) {
            return { ok: false, reason: 'Horario fechado.' };
        }

        return { ok: true, reason: '' };
    }, [selectedRange, settingsAgenda?.closedSlots, settingsAgenda?.horario]);

    const validateRangeAgainstAgendaSettings = () => {
        if (rangeOfferEligibility.ok) return true;
        if (rangeOfferEligibility.reason === 'Horario fechado.') {
            errorSnackbar('Esse horario esta fechado na agenda.');
            return false;
        }
        if (rangeOfferEligibility.reason === 'Fora da janela configurada.') {
            errorSnackbar('Horario fora da janela configurada do atendente.');
            return false;
        }
        errorSnackbar('Selecione uma faixa valida.');
        return false;
    };

    useEffect(() => {
        if (!openOffer) return;
        setOfferDiscountPercent(0);
        const loadInitialData = async () => {
            try {
                setClientsLoading(true);
                const [quotaRes, clientsRes] = await Promise.all([
                    ofertasService.quota(offerDate),
                    userService.getClients('', undefined, attendantId, true),
                ]);

                setOfferQuota(quotaRes.data);
                const options: ClientOption[] = (clientsRes.data || [])
                    .filter((client: any) => String(client?.phoneNumber || '').trim().length > 0) // so quem tem WhatsApp
                    .map((client: any) => ({
                        label: client.displayName,
                        value: client.uid,
                        photoURL: client.photoURL,
                        phoneNumber: client.phoneNumber,
                    }));
                setClientsAll(options);
            } catch (e) {
                setOfferQuota(null);
                setClientsAll([]);
            } finally {
                setClientsLoading(false);
            }
        };

        loadInitialData();
    }, [attendantId, openOffer, offerDate]);

    const submitOffer = async () => {
        if (!selectedRange) return;
        if (!validateRangeAgainstAgendaSettings()) return;
        if (!clientsSelected.length) {
            errorSnackbar(`Selecione ate ${maxSelectableClients} clientes.`);
            return;
        }
        if (clientsSelected.length > remainingQuota) {
            errorSnackbar(`Limite diario excedido. Restante hoje: ${remainingQuota}.`);
            return;
        }
        if (clientsSelected.length > 10) {
            errorSnackbar('Selecione ate 10 clientes.');
            return;
        }

        try {
            setOfferLoading(true);
            const res = await ofertasService.create({
                attendantId,
                date: offerDate,
                startMinutes: selectedRange.start,
                endMinutes: selectedRange.end,
                discountPercent: offerDiscountPercent,
                clientIds: clientsSelected,
            });

            const queued = res?.data?.send?.queued ?? 0;
            successSnackbar(`Oferta enviada. Mensagens enfileiradas: ${queued}.`);
            window.dispatchEvent(new Event('offers:refresh'));

            closeOfferModal();
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Erro ao enviar oferta.';
            errorSnackbar(msg);
        } finally {
            setOfferLoading(false);
        }
    };

    const toggleClientSelected = (clientId: string) => {
        setClientsSelected((prev) => {
            const exists = prev.includes(clientId);
            if (exists) return prev.filter((id) => id !== clientId);

            if (maxSelectableClients <= 0) {
                errorSnackbar('Sem quota disponivel hoje.');
                return prev;
            }
            if (prev.length >= maxSelectableClients) {
                errorSnackbar(`Selecione ate ${maxSelectableClients} clientes.`);
                return prev;
            }

            return [...prev, clientId];
        });
    };

    const clearClientsSelected = () => setClientsSelected([]);
    const selectedRangeText = selectedRange
        ? `${formatTime(selectedRange.start)} - ${formatTime(selectedRange.end)}`
        : '';
    const actionButtonBaseClass =
        'group relative flex h-auto min-h-[3.75rem] w-16 flex-col items-center justify-center gap-1 rounded-2xl border px-2 py-2 text-center shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 sm:h-11 sm:min-h-0 sm:w-11 sm:gap-0 sm:px-0 sm:py-0';
    const closeOfferModal = () => {
        setOpenOffer(false);
        setClientsSelected([]);
        setClientsSearch('');
        setSelectedRange(undefined);
    };

    const submit = async () => {
        try {
            setLoading(true);

            if (!!selectedRange) {
                await atendentesService.addClosedSlots({
                    date: agenda.date,
                    atendenteId: attendantId,
                    closedSlots: selectedRange,
                });
            }

            setSelectedRange(undefined);
            loadSettings();
            successSnackbar('Agenda salva com sucesso!');
        } catch (e) {
            console.error('Erro ao salvar:', e);
            errorSnackbar('Ocorreu um erro ao salvar a agenda.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full rounded-[1.75rem] border border-base-200/80 bg-base-100 p-2.5 shadow-[0_20px_45px_-28px_rgba(15,23,42,0.45)] sm:w-auto">
            <div className="mb-2 rounded-2xl border border-base-200/80 bg-base-200/70 px-3 py-2 text-center">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-base-content/45">
                    Seleção
                </div>
                <div className="mt-0.5 text-sm font-semibold text-base-content">
                    {selectedRangeText}
                </div>
            </div>

            <div className="flex items-stretch justify-center gap-2 sm:flex-col sm:items-center">
                {!hasConflict && (
                    <button
                        onClick={() => setOpenCreate(true)}
                        className={`${actionButtonBaseClass} border-primary/20 bg-primary text-primary-content hover:bg-primary/95`}
                        title="Criar agendamento"
                    >
                        <Plus className="text-base" aria-hidden="true" />
                        <span className="text-[9px] font-semibold uppercase tracking-[0.08em] leading-none sm:hidden">
                            Criar
                        </span>
                    </button>
                )}

                <button
                    className={`${actionButtonBaseClass} border-error/15 bg-error/10 text-error hover:bg-error/15`}
                    title="Fechar horário"
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen(true);
                    }}
                >
                    <Lock className="text-[15px]" aria-hidden="true" />
                    <span className="text-[9px] font-semibold uppercase tracking-[0.08em] leading-none sm:hidden">
                        Fechar
                    </span>
                </button>

                {!hasConflict && rangeOfferEligibility.ok && (
                    <button
                        className={`${actionButtonBaseClass} border-success/20 bg-success/12 text-success hover:bg-success/18`}
                        title="Compartilhar horário"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!validateRangeAgainstAgendaSettings()) return;
                            setOpenOffer(true);
                        }}
                    >
                        <Megaphone className="text-[15px]" aria-hidden="true" />
                        <span className="text-[9px] font-semibold uppercase tracking-[0.08em] leading-none sm:hidden">
                            Oferta
                        </span>
                    </button>
                )}

                <div className="my-0.5 hidden h-px w-8 bg-base-300/80 sm:block"></div>

                <button
                    className={`${actionButtonBaseClass} border-base-200 bg-base-100 text-base-content/70 hover:bg-base-200/70`}
                    title="Cancelar seleção"
                    onClick={() => setSelectedRange(undefined)}
                >
                    <X className="text-base" aria-hidden="true" />
                    <span className="text-[9px] font-semibold uppercase tracking-[0.08em] leading-none sm:hidden">
                        Cancelar
                    </span>
                </button>
            </div>

            {/* 🪄 Modal de confirmação */}
            {open && (
                <Modal
                    onClose={() => {
                        setOpen(false);
                        setSelectedRange(undefined)
                    }}
                    title="Fechar Horários"
                    subTitle={
                        <div className="text-center text-base-content/70 mt-2">
                            <div className="text-lg font-semibold">
                                {`Deseja realmente fechar o horário `}
                                <span className="text-primary">
                  {formatTime(selectedRange?.start)} - {formatTime(selectedRange?.end)}
                </span>
                            </div>
                            <div className="mt-1 text-sm">
                                {moment(agenda.date).format('dddd')},{' '}
                                {moment(agenda.date).format('DD [de] MMMM')}
                            </div>
                        </div>
                    }
                >
                    <div className="flex justify-between mt-6">
                        <button
                            className="btn btn-ghost w-1/2 mr-2"
                            onClick={() => {
                                setSelectedRange(undefined)
                                setOpen(false)
                            }}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-primary w-1/2 ml-2"
                            onClick={submit}
                            disabled={loading}
                        >
                            {loading ? <Loading /> : 'Confirmar'}
                        </button>
                    </div>
                </Modal>
            )}

            {openCreate && (
                <Modal
                    onClose={() => {
                        setSelectedRange(undefined)
                        setOpenCreate(false);
                    }}
                    title="Criar agendamento"
                    subTitle={
                        <div className="text-center text-base-content/70">
                            <div className="mt-1 text-sm">
                                {moment(agenda.date).format('dddd')},{' '}
                                {moment(agenda.date).format('DD [de] MMMM')}
                                <span className="text-primary ml-2">
                                  {formatTime(selectedRange?.start)} - {formatTime(selectedRange?.end)}
                                </span>
                            </div>
                        </div>
                    }
                >
                    <AgendaForm
                        doc={{
                            atendente_id: attendantId,
                            duration: (selectedRange?.end || 0) - (selectedRange?.start || 0),
                            startAt: date.toDate(),
                            startMinutes: selectedRange?.start,
                        }}
                        onDone={() => {
                            setOpenCreate(false);
                            setSelectedRange(undefined);
                        }}
                        redirectOnCreate={false}
                        forceFloatingButton
                    />

                    <div className="flex justify-center">
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={() => {
                                setOpenCreate(false);
                                setSelectedRange(undefined)
                            }}
                        >
                            Cancelar
                        </button>
                    </div>
                </Modal>
            )}

            {openOffer && (
                <Modal
                    onClose={closeOfferModal}
                    title="Compartilhar horário"
                    desktopClassName="min-w-[860px]"
                    subTitle={
                        <div className="text-center text-base-content/70">
                            <div className="mt-1 text-sm">
                                {moment(agenda.date).format('dddd')},{' '}
                                {moment(agenda.date).format('DD [de] MMMM')}
                                <span className="text-primary ml-2">
                                  {formatTime(selectedRange?.start)} - {formatTime(selectedRange?.end)}
                                </span>
                            </div>
                            <div className="mt-1 text-xs text-base-content/60">
                                {offerQuota ? `Envios hoje: ${offerQuota.used}/${offerQuota.limit} (restante ${offerQuota.remaining})` : 'Carregando cota...'}
                            </div>
                        </div>
                    }
                >
                    <div className="space-y-3">
                        <div className="rounded-xl border border-base-200/70 p-3">
                            <div className="flex items-center justify-between gap-2">
                                <div>
                                    <div className="text-sm font-semibold">
                                        Clientes (até {maxSelectableClients})
                                    </div>
                                    <div className="mt-0.5 text-xs text-base-content/50">
                                        Mostrando apenas clientes com telefone cadastrado (WhatsApp).
                                    </div>
                                </div>
                                <div className="text-xs text-base-content/60">
                                    Selecionados: {clientsSelected.length}
                                </div>
                            </div>

                            <div className="mt-3 flex gap-2">
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Buscar por nome ou telefone..."
                                    value={clientsSearch}
                                    onChange={(e) => setClientsSearch(e.target.value)}
                                    disabled={offerLoading}
                                />
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={clearClientsSelected}
                                    disabled={offerLoading || clientsSelected.length === 0}
                                    title="Limpar selecionados"
                                >
                                    Limpar
                                </button>
                            </div>

                            <div className="mt-3 max-h-80 overflow-y-auto">
                                {clientsLoading ? (
                                    <div className="py-6"><Loading /></div>
                                ) : clientsOptions.length === 0 ? (
                                    <div className="py-6 text-center text-sm text-base-content/60">
                                        Nenhum cliente encontrado.
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {clientsOptions.map((client) => {
                                            const name = String(client?.label || '').trim() || 'Sem nome';
                                            const initials = name ? name[0].toUpperCase() : '?';
                                            const phone = String(client?.phoneNumber || '').trim();
                                            const checked = clientsSelected.includes(client.value);
                                            const disabled =
                                                offerLoading ||
                                                (!checked && clientsSelected.length >= maxSelectableClients);

                                            return (
                                                <div
                                                    key={client.value}
                                                    role="button"
                                                    tabIndex={0}
                                                    className={`flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-base-200/40 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                                    onClick={() => {
                                                        if (disabled) return;
                                                        toggleClientSelected(client.value);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (disabled) return;
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            toggleClientSelected(client.value);
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm"
                                                        checked={checked}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => {
                                                            if (disabled) return;
                                                            toggleClientSelected(client.value);
                                                        }}
                                                        disabled={disabled}
                                                    />
                                                    <div className="avatar">
                                                        <div className="w-9 rounded-full bg-base-200">
                                                            {client.photoURL ? (
                                                                <img src={client.photoURL} alt={name} loading="lazy" />
                                                            ) : (
                                                                <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-base-content/70">
                                                                    {initials}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="truncate text-sm font-medium">{name}</div>
                                                        <div className="truncate text-xs text-base-content/60">
                                                            {formatPhone(phone)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Desconto (%)</span>
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={100}
                                className="input input-bordered"
                                value={offerDiscountPercent}
                                onChange={(e) => setOfferDiscountPercent(Number(e.target.value))}
                                disabled={offerLoading}
                            />
                            <div className="mt-1 text-xs text-base-content/60">
                                Desconto aplicado para serviços elegíveis desta oferta.
                            </div>
                        </div>

                        <div className="flex justify-between gap-2 pt-2">
                            <button
                                type="button"
                                className="btn btn-ghost w-1/2"
                                onClick={closeOfferModal}
                                disabled={offerLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary w-1/2"
                                onClick={submitOffer}
                                disabled={offerLoading}
                            >
                                {offerLoading ? <Loading /> : 'Enviar'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};
